
import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import type { UserTask } from '@/types/tasks'

export const useUserTasks = () => {
  const [userTasks, setUserTasks] = useState<UserTask[]>([])
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()

  const fetchUserTasks = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session) {
        setLoading(false)
        return
      }

      const { data: userTasksData, error: userError } = await supabase
        .from('user_tasks')
        .select(`
          *,
          school_tasks (*)
        `)
        .eq('user_id', session.user.id)

      if (userError) throw userError
      setUserTasks(userTasksData || [])
    } catch (error: any) {
      console.error('Error fetching user tasks:', error)
      toast({
        title: "Error",
        description: "Failed to load user tasks",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUserTasks()

    // Set up real-time subscription
    const channel = supabase
      .channel('user-tasks-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_tasks'
        },
        () => {
          fetchUserTasks()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [])

  return {
    userTasks,
    loading,
    refreshUserTasks: fetchUserTasks
  }
}
